import React, { useState, useRef, useEffect } from 'react';
import { Button } from './button';
import { Textarea } from './textarea';
import { Label } from './label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Maximize2, Minimize2, Type, AlignLeft, Eye } from 'lucide-react';

/**
 * ExpandableTextarea - A textarea that auto-expands and supports full-screen editing
 * 
 * Features:
 * - Auto-expands as content grows (min 4 rows, max configurable)
 * - Full-screen edit mode for large content
 * - Word and character count
 * - Optional markdown preview (coming soon)
 */
const ExpandableTextarea = ({
  value,
  onChange,
  label,
  required = false,
  placeholder = '',
  minRows = 4,
  maxRows = 20,
  showCounts = true,
  className = '',
  id,
  ...props
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');
  const textareaRef = useRef(null);
  const fullscreenRef = useRef(null);

  // Sync local value with prop
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  // Auto-resize textarea based on content
  const autoResize = (textarea) => {
    if (!textarea) return;
    
    // Reset height to calculate new height
    textarea.style.height = 'auto';
    
    // Calculate line height (approximately 24px per line)
    const lineHeight = 24;
    const minHeight = minRows * lineHeight;
    const maxHeight = maxRows * lineHeight;
    
    // Set new height within bounds
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight}px`;
    
    // Add overflow if content exceeds max
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  };

  // Auto-resize on value change
  useEffect(() => {
    if (textareaRef.current) {
      autoResize(textareaRef.current);
    }
  }, [localValue, minRows, maxRows]);

  // Auto-resize fullscreen textarea
  useEffect(() => {
    if (isFullscreen && fullscreenRef.current) {
      fullscreenRef.current.style.height = '100%';
    }
  }, [isFullscreen]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(e);
  };

  const handleFullscreenSave = () => {
    setIsFullscreen(false);
  };

  // Calculate counts
  const charCount = localValue.length;
  const wordCount = localValue.trim() ? localValue.trim().split(/\s+/).length : 0;
  const lineCount = localValue ? localValue.split('\n').length : 0;

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <div className="flex items-center justify-between">
          <Label htmlFor={id}>
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsFullscreen(true)}
            className="h-7 px-2 text-gray-500 hover:text-gray-700"
            title="Expand to full screen"
          >
            <Maximize2 size={14} className="mr-1" />
            <span className="text-xs">Expand</span>
          </Button>
        </div>
      )}
      
      <div className="relative">
        <Textarea
          ref={textareaRef}
          id={id}
          value={localValue}
          onChange={handleChange}
          placeholder={placeholder}
          required={required}
          className="resize-none transition-all duration-200"
          style={{ minHeight: `${minRows * 24}px` }}
          {...props}
        />
      </div>

      {showCounts && (
        <div className="flex items-center justify-between text-xs text-gray-400">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Type size={12} />
              {charCount.toLocaleString()} chars
            </span>
            <span className="flex items-center gap-1">
              <AlignLeft size={12} />
              {wordCount.toLocaleString()} words
            </span>
            {lineCount > 1 && (
              <span>{lineCount} lines</span>
            )}
          </div>
          {charCount > 5000 && (
            <span className="text-amber-500">Large content - consider using full screen mode</span>
          )}
        </div>
      )}

      {/* Full Screen Dialog */}
      <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center justify-between">
              <span>{label || 'Edit Content'}</span>
              <div className="flex items-center gap-2 text-sm font-normal text-gray-500">
                <span>{charCount.toLocaleString()} chars</span>
                <span>•</span>
                <span>{wordCount.toLocaleString()} words</span>
                <span>•</span>
                <span>{lineCount} lines</span>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 min-h-0 mt-4">
            <Textarea
              ref={fullscreenRef}
              value={localValue}
              onChange={handleChange}
              placeholder={placeholder}
              className="h-full resize-none text-base leading-relaxed"
              style={{ 
                fontFamily: 'Inter, system-ui, sans-serif',
                minHeight: '100%'
              }}
            />
          </div>
          
          <div className="flex-shrink-0 flex items-center justify-between pt-4 border-t mt-4">
            <p className="text-sm text-gray-500">
              💡 Tip: Paste large documents directly. Content auto-saves as you type.
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsFullscreen(false)}
              >
                <Minimize2 size={16} className="mr-2" />
                Close
              </Button>
              <Button
                type="button"
                onClick={handleFullscreenSave}
              >
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { ExpandableTextarea };
export default ExpandableTextarea;
